import { Box, Flex } from '@sequencehq/core-components'
import { match } from 'ts-pattern'
import { useNavigate } from 'react-router-dom'
import { ErrorBoundary } from 'common/components/ErrorBoundary/ErrorBoundary'
import { useAtomValue, useSetAtom } from 'jotai'
import { automationsPaths } from 'Automations/router'
import { InboxList } from './InboxList'
import { useSelectedManualTask } from 'Automations/watchtower/inbox/manualTasks/hooks/useSelectedManualTask'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useManualTasks } from 'Automations/watchtower/inbox/manualTasks/hooks/useManualTasks'
import { ManualTaskItem } from 'Automations/watchtower/inbox/manualTasks/manualTasksInbox/components/manualTasksList/ManualTaskItem'
import {
  isNotification,
  NotificationItem
} from 'Automations/watchtower/inbox/notifications'
import { isRecognisedTaskType } from 'Automations/watchtower/inbox/isRecognisedTaskType'
import { ListWithPagination } from 'Automations/components'
import { AdminSettingsButton } from 'Automations/components'
import { useCallback, useEffect, useMemo } from 'react'
import { ManualTask } from 'Automations/watchtower/inbox/manualTasks/types'
import { optimisticallyViewedTaskIdsAtom } from 'Automations/watchtower/inbox/manualTasks/manualTasksInbox/atoms'
import { watchtowerFiltersAtom } from 'Automations/watchtower/atoms'
import { useIsMobile } from 'hooks/useIsMobile'
import { ListView } from '../inbox.types'
import { AddFilter } from './AddFilter'
import { FilterPills } from './FilterPills'

export const TasksList = ({ view }: { view: ListView }) => {
  const { status: statusFilters } = useAtomValue(watchtowerFiltersAtom)

  // needsAction fixes the status to PENDING; the All tab reads the manual-task-
  // native status filter straight from the atom (no execution-status mapping),
  // and omits it entirely (all statuses) when nothing is selected.
  const status = match(view)
    .with('needsAction', (): string | undefined => 'PENDING')
    .with('all', () =>
      statusFilters.length > 0 ? statusFilters.join(',') : undefined
    )
    .exhaustive()

  const {
    isPending,
    isError,
    isFetchingNextPage,
    manualTasks,
    hasNextPage,
    fetchNextPage
  } = useManualTasks({ status })
  const { selectedManualTask, isPending: selectedManualTaskIsPending } =
    useSelectedManualTask()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const setOptimisticallyViewedTaskIds = useSetAtom(
    optimisticallyViewedTaskIdsAtom
  )

  const navigatePathForTask = useCallback(
    (task: ManualTask) =>
      match(view)
        .with('all', () =>
          isNotification(task.type)
            ? automationsPaths.allNotificationInboxPreview(task.id)
            : automationsPaths.allTaskInboxPreview(task.id)
        )
        .with('needsAction', () =>
          isNotification(task.type)
            ? automationsPaths.notificationInboxPreview(task.id)
            : automationsPaths.manualTaskInboxPreview(task.id)
        )
        .exhaustive(),
    [view]
  )

  const emptySelectionPath = match(view)
    .with('all', () => automationsPaths.executionInbox())
    .with('needsAction', () => automationsPaths.manualTasksInbox())
    .exhaustive()

  const markAsRead = useCallback(
    (task: ManualTask) => {
      if (!task.viewedAt) {
        setOptimisticallyViewedTaskIds((prev: Set<string>) =>
          new Set(prev).add(task.id)
        )
        dashboardv99990101Client
          .postMarkManualTaskAsRead({
            id: task.id
          })
          .then(res => {
            if (res.error) {
              setOptimisticallyViewedTaskIds((prev: Set<string>) => {
                const next = new Set(prev)
                next.delete(task.id)
                return next
              })
            }
          })
      }
    },
    [setOptimisticallyViewedTaskIds]
  )

  // Deduplicate manual tasks: keep only the latest per execution, and filter
  // out tasks whose type has no matching list component.
  const deduplicatedManualTasks = useMemo(() => {
    const latestByExecution = manualTasks.reduce<
      Map<string | undefined, (typeof manualTasks)[number]>
    >((acc, task) => {
      if (!isRecognisedTaskType(task.type)) return acc

      const key = task.executorWorkflowExecutionId || task.id
      const existing = acc.get(key)
      const isNewer =
        !existing ||
        new Date(task.createdAt).getTime() >
          new Date(existing.createdAt).getTime()
      if (isNewer) acc.set(key, task)
      return acc
    }, new Map())

    return Array.from(latestByExecution.values())
  }, [manualTasks])

  /**
   * Keep the selection in sync with the list:
   * 1. First navigating to the screen (no task selected yet) — select the first
   * 2. The currently selected task is no longer visible in the list (e.g. it was
   *    completed or dismissed) — move to the first remaining task, or clear the
   *    stale selection when the list is now empty so the preview pane returns to
   *    its empty state rather than keeping the completed task's preview
   */
  const allManualTaskIds = useMemo(
    () => deduplicatedManualTasks.map(task => task.id),
    [deduplicatedManualTasks]
  )

  useEffect(() => {
    // A failed list fetch surfaces as an empty list (the query has no data on
    // error), so pause the sync rather than clear or move a selection whose
    // own task fetch may have succeeded — the poll retries and recovers.
    if (isPending || selectedManualTaskIsPending || isError) {
      return
    }

    const selectionIsStale =
      !selectedManualTask?.id ||
      !allManualTaskIds.includes(selectedManualTask.id)

    if (!selectionIsStale) {
      return
    }

    // On mobile the inbox shows one pane at a time, so we never auto-open the
    // first task. We still clear a stale selection (a just-completed task whose
    // route param lingers) so the user returns to the list rather than being
    // stranded on an empty preview.
    if (isMobile) {
      if (selectedManualTask?.id) {
        navigate(emptySelectionPath, { replace: true })
      }
      return
    }

    const firstTask = deduplicatedManualTasks.at(0)

    if (!firstTask) {
      // Nothing left to select. Clear a stale selection (a just-completed task
      // whose route param lingers) so the preview resets; guard on there being
      // a selection to avoid a redundant navigate on the initial empty render.
      if (selectedManualTask?.id) {
        navigate(emptySelectionPath, { replace: true })
      }
      return
    }

    navigate(navigatePathForTask(firstTask), {
      replace: true
    })
    markAsRead(firstTask)
  }, [
    isMobile,
    isPending,
    isError,
    selectedManualTaskIsPending,
    allManualTaskIds,
    deduplicatedManualTasks,
    navigate,
    selectedManualTask,
    markAsRead,
    navigatePathForTask,
    emptySelectionPath
  ])

  return (
    <InboxList
      isPending={isPending}
      headerActions={
        <Flex gap="8px" alignItems="center">
          <AddFilter />
          <AdminSettingsButton
            onToggleOff={() => navigate(emptySelectionPath)}
          />
        </Flex>
      }
      filterRow={<FilterPills />}
    >
      <>
        {deduplicatedManualTasks.length === 0 ? (
          <InboxList.EmptyState />
        ) : (
          <ListWithPagination
            isLoading={isFetchingNextPage}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
          >
            {deduplicatedManualTasks.map(task => {
              const navigatePath = navigatePathForTask(task)

              return (
                <ErrorBoundary name="TasksList.item" key={task.id}>
                  <Box data-testid={`watchtower.manualTasks.${task.id}`}>
                    {isNotification(task.type) ? (
                      <NotificationItem
                        manualTask={task}
                        isSelected={selectedManualTask?.id === task.id}
                        onClick={e => {
                          markAsRead(task)
                          navigate(navigatePath)
                          e.stopPropagation()
                        }}
                      />
                    ) : (
                      <ManualTaskItem
                        manualTask={task}
                        isSelected={selectedManualTask?.id === task.id}
                        onClick={e => {
                          markAsRead(task)
                          navigate(navigatePath)
                          e.stopPropagation()
                        }}
                      />
                    )}
                  </Box>
                </ErrorBoundary>
              )
            })}
          </ListWithPagination>
        )}
      </>
    </InboxList>
  )
}
